
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Link, useNavigate } from 'react-router-dom';
import { Article } from '@/hooks/journalist/types';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tag } from 'lucide-react';

interface ArticleCardProps {
  article: Article;
  layout?: 'horizontal' | 'vertical';
  showLead?: boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ 
  article, 
  layout = 'vertical',
  showLead = true
}) => {
  const navigate = useNavigate();
  
  // Extract first two letters of author name for avatar fallback
  const authorInitials = article.author_name 
    ? article.author_name.substring(0, 2).toUpperCase() 
    : article.author?.substring(0, 2).toUpperCase() || 'AU';

  // Truncate title to 50 characters
  const truncatedTitle = article.title.length > 50 
    ? article.title.substring(0, 50) + '...' 
    : article.title;

  // Truncate lead text to 140 characters if it exists
  const truncatedLead = article.lead 
    ? article.lead.length > 140 
      ? article.lead.substring(0, 140) + '...' 
      : article.lead 
    : '';
    
  // Handle tag click - navigate to tag view
  const handleTagClick = (e: React.MouseEvent, tagName: string) => {
    e.preventDefault(); // Prevent the article link from being followed
    e.stopPropagation(); // Stop event bubbling
    navigate(`/tags/${encodeURIComponent(tagName)}`);
  };

  return (
    <Link to={`/article/${article.id}`} className="block h-full">
      <Card className={`overflow-hidden hover:shadow-md transition-shadow h-full ${
        layout === 'horizontal' ? 'flex flex-col md:flex-row' : 'flex flex-col'
      }`}>
        <div className={layout === 'horizontal' ? 'md:w-1/3' : 'w-full'}>
          <img 
            src={article.image} 
            alt={article.title} 
            className={`${layout === 'horizontal' ? 'h-48 md:h-full' : 'h-48'} w-full object-cover`}
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=500&h=350&auto=format&fit=crop';
            }}
          />
        </div>
        <CardContent className={`p-4 flex-grow ${layout === 'horizontal' ? 'md:w-2/3' : 'w-full'}`}>
          <div className="flex items-center mb-2">
            <Badge 
              variant="outline" 
              className="text-xs flex items-center gap-1 cursor-pointer hover:bg-muted"
              onClick={(e) => handleTagClick(e, article.category)}
            >
              <Tag size={12} />
              {article.category}
            </Badge>
            {article.premium && (
              <Badge variant="secondary" className="ml-2 text-xs">Premium</Badge>
            )}
          </div>
          <h3 className="text-lg font-bold mb-1">{truncatedTitle}</h3>
          {showLead && truncatedLead && (
            <p className="text-sm text-muted-foreground mb-2">{truncatedLead}</p>
          )}
          {article.excerpt && (
            <p className="text-sm text-muted-foreground mb-3">{article.excerpt}</p>
          )}
          <div className="flex items-center justify-between mt-auto pt-2">
            <div className="flex items-center">
              <Avatar className="h-6 w-6 mr-2">
                <AvatarImage 
                  src={article.author_avatar} 
                  alt={article.author_name || article.author || "Author"} 
                />
                <AvatarFallback>{authorInitials}</AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium">{article.author_name || article.author}</span>
            </div>
            <span className="text-xs text-muted-foreground">{article.readTime} min read</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ArticleCard;
